const API_URL = process.env.REACT_APP_API_URL || 'https://api.organicgreenshub.com';

class AuthService {
  async checkEmailExists(email) {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        throw new Error('Failed to check email');
      }
      
      const data = await response.json();
      return data.exists;
    } catch (error) {
      throw error;
    }
  }
  
  async checkPhoneExists(phone) {
    try {
      const response = await fetch(`${API_URL}/api/auth/check-phone`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone })
      });
      
      if (!response.ok) {
        throw new Error('Failed to check phone');
      }
      
      const data = await response.json();
      return data.exists;
    } catch (error) {
      throw error;
    }
  }
  
  async login(identifier, password, loginType = 'email') {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          [loginType]: identifier, 
          password,
          loginType 
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }
      
      const data = await response.json();
      this.setToken(data.token);
      this.setUser(data.user);
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async sendOTP(email) {
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send OTP');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async verifyOTP(email, otp) {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to verify OTP');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async register(email, password, name, phone, isAdmin, otp, timezone, address) {
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name, phone, isAdmin, otp, timezone, address })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Registration failed');
      }
      
      const data = await response.json();
      this.setToken(data.token);
      this.setUser(data.user);
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async verifyToken() {
    const token = this.getToken();
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) return false;
      
      const data = await response.json();
      return data.valid;
    } catch (error) {
      return false;
    }
  }
  
  logout() {
    // Check if running as PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  window.navigator.standalone || 
                  document.referrer.includes('android-app://');
    
    if (isPWA) {
      // For PWA, don't logout - just redirect to home
      console.log('PWA detected - maintaining session');
      return false; // Indicate logout was prevented
    }
    
    // For browser, normal logout
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('customerId');
    sessionStorage.removeItem('customerEmail');
    return true; // Indicate logout was successful
  }
  
  setToken(token) {
    localStorage.setItem('token', token);
  }
  
  getToken() {
    return localStorage.getItem('token');
  }
  
  setUser(user) {
    localStorage.setItem('user', JSON.stringify(user));
    // Also set session storage for backward compatibility
    sessionStorage.setItem('customerId', user.id);
    sessionStorage.setItem('customerEmail', user.email);
  }
  
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }
  
  isAuthenticated() {
    return !!this.getToken();
  }
  
  isAdmin() {
    const user = this.getUser();
    return user?.role === 'admin';
  }
  
  getCurrentUserId() {
    const user = this.getUser();
    return user?.id || sessionStorage.getItem('customerId');
  }
  
  async loginWithGoogle() {
    try {
      // Import Google auth service
      const googleAuthService = (await import('./google-auth-service')).default;
      
      // Sign in with Google
      const googleResult = await googleAuthService.signIn();
      
      if (!googleResult.success) {
        throw new Error(googleResult.error || 'Google sign-in failed');
      }
      
      // Send Google user data to backend for registration/login
      const response = await fetch(`${API_URL}/api/auth/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: googleResult.user.email,
          name: googleResult.user.name,
          googleId: googleResult.user.id,
          imageUrl: googleResult.user.imageUrl,
          idToken: googleResult.user.idToken
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Google login failed');
      }
      
      const data = await response.json();
      this.setToken(data.token);
      this.setUser(data.user);
      
      return {
        success: true,
        token: data.token,
        user: {
          id: data.user.id,
          email: data.user.email,
          fname: data.user.name?.split(' ')[0] || data.user.name,
          lname: data.user.name?.split(' ').slice(1).join(' ') || '',
          isAdmin: data.user.role === 'admin'
        },
        freeTrialActivated: data.freeTrialActivated || false
      };
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }
  
  async forgotPassword(email) {
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to process request');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reset password');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }
  
  async verifySuperUser(password) {
    try {
      const token = this.getToken();
      const response = await fetch(`${API_URL}/api/auth/verify-superuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Invalid super user password');
      }
      
      const data = await response.json();
      console.log('[AUTH] Super user verified');
      return { success: true, ...data };
    } catch (error) {
      console.error('[AUTH] Super user verification failed:', error.message);
      throw error;
    }
  }
}

export default new AuthService();
